import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div
        className="py-14 px-6 text-white"
        style={{
          background: 'linear-gradient(135deg, #F06F6B 0%, #F07A5E 40%, #F58A54 100%)',
        }}
      >
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold mb-1">Privacy Policy</h1>
          <p className="text-white/90 text-base font-medium mb-2">Atlas Procurement Services, LLC DBA Stackd</p>
          <p className="text-white/80 text-sm">Last Updated: 3/12/2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12">

          {/* Intro */}
          <p className="text-gray-700 leading-relaxed mb-8">
            Atlas Procurement Services, LLC DBA Stackd ("Stackd Financial," "Stackd," "we," "our," or "us") operates the Stackd platform, which helps users research, compare, and organize major purchases, financial products, and services.
          </p>
          <p className="text-gray-700 leading-relaxed mb-8">
            This Privacy Policy describes how we collect, use, share, and protect information when you access or use our website, applications, and services (collectively, the "Platform").
          </p>

          {/* Section 1 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">1. Collection of Information</h2>

            <h3 className="font-semibold text-gray-800 mb-2">Information You Provide to Us</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              You may voluntarily provide information when you:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 pl-2">
              <li>Create an account</li>
              <li>Submit a request for quotes</li>
              <li>Research purchases</li>
              <li>Save products or vendors</li>
              <li>Contact us for support</li>
              <li>Quote a project</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-3">This information may include:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6 pl-2">
              <li>Name</li>
              <li>Email address</li>
              <li>Location (city, state, ZIP code)</li>
              <li>Purchase interests or project details</li>
              <li>Vendor capabilities</li>
              <li>Preferences related to products or services</li>
              <li>Other information you choose to provide</li>
            </ul>

            <h3 className="font-semibold text-gray-800 mb-2">Information Collected Automatically</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              When you use our Platform, we may automatically collect certain information about your device and activity, including:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6 pl-2">
              <li>IP address</li>
              <li>Device type</li>
              <li>Browser type</li>
              <li>Operating system</li>
              <li>Pages viewed</li>
              <li>Interactions with platform features</li>
              <li>Referring URLs</li>
              <li>Approximate location information</li>
            </ul>

            <h3 className="font-semibold text-gray-800 mb-2">Cookies and Similar Technologies</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              We and our partners may use cookies and similar technologies to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 pl-2">
              <li>Recognize returning users</li>
              <li>Remember preferences</li>
              <li>Measure site performance</li>
              <li>Track affiliate referrals</li>
              <li>Improve platform functionality</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-2">
              Some third-party partners may place cookies when you click outbound links from the Platform.
            </p>
            <p className="text-gray-700 leading-relaxed">
              More details are available in our Cookie Policy.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">2. Use of Information We Collect</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              We use information we collect to operate and improve the Stackd Platform.
            </p>
            <p className="text-gray-700 leading-relaxed mb-3">Examples include:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 pl-2">
              <li>Providing and maintaining the Platform</li>
              <li>Helping users research products and services</li>
              <li>Personalizing recommendations and search results</li>
              <li>Analyzing platform usage and performance</li>
              <li>Enabling referrals to third-party providers</li>
              <li>Improving platform features and usability</li>
              <li>Communicating service updates or support messages</li>
              <li>Preventing fraud and protecting platform security</li>
            </ul>
            <p className="text-gray-700 leading-relaxed font-medium">
              Stackd does not sell personally identifiable information to third parties.
            </p>
          </section>

          {/* Section 3 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">3. Disclosure of Information</h2>

            <h3 className="font-semibold text-gray-800 mb-2">Service Providers:</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              We may share information with vendors that help operate the Platform, such as:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6 pl-2">
              <li>Hosting providers</li>
              <li>Analytics providers</li>
              <li>Infrastructure providers</li>
              <li>Customer support services</li>
              <li>Fraud prevention services</li>
            </ul>

            <h3 className="font-semibold text-gray-800 mb-2">Third-Party Providers:</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              If you choose to interact with third-party offers on Stackd—such as requesting a quote, applying for a financial product, or purchasing a product—we may share certain information necessary to facilitate that request.
            </p>
            <p className="text-gray-700 leading-relaxed mb-3">Examples include:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 pl-2">
              <li>Lenders</li>
              <li>Insurance providers</li>
              <li>Retailers</li>
              <li>Service providers</li>
              <li>Financing partners</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-6">
              Each provider operates under its own privacy policy.
            </p>

            <h3 className="font-semibold text-gray-800 mb-2">Legal Compliance</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              We may disclose information if required to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 pl-2">
              <li>Comply with legal obligations</li>
              <li>Respond to lawful requests by public authorities</li>
              <li>Protect the rights or safety of Stackd Financial, our users, or others</li>
              <li>Investigate fraud or misuse of the Platform</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">4. Your Choices</h2>
            <p className="text-gray-700 leading-relaxed mb-3">You may:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 pl-2">
              <li>Choose not to provide certain information</li>
              <li>Disable cookies through your browser settings</li>
              <li>Stop using the Platform at any time</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Please note that disabling certain cookies or limiting information may affect how some platform features function.
            </p>
          </section>

          {/* Section 5 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">5. Your Privacy Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Depending on your location, you may have rights regarding your personal information.
            </p>
            <p className="text-gray-700 leading-relaxed mb-3">
              These rights may include the ability to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 pl-2">
              <li>Request access to your personal information</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of certain personal information</li>
              <li>Request information about how your data is used</li>
              <li>Opt out of certain data sharing practices</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              To exercise these rights, you may contact us using the information provided below.
            </p>
          </section>

          {/* Section 6 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">6. Other Notices</h2>

            <h3 className="font-semibold text-gray-800 mb-2">Third-Party Links</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              The Stackd Platform may contain links to third-party websites or services. When you click these links, you may leave our Platform and be subject to the policies of those third parties.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              Stackd is not responsible for the privacy practices of third-party websites.
            </p>

            <h3 className="font-semibold text-gray-800 mb-2">Data Security</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              We implement reasonable administrative, technical, and organizational safeguards to protect the information we collect. However, no internet transmission or electronic storage method is completely secure.
            </p>

            <h3 className="font-semibold text-gray-800 mb-2">Children's Privacy</h3>
            <p className="text-gray-700 leading-relaxed">
              The Platform is intended for individuals 18 years of age or older. Stackd does not knowingly collect personal information from children under the age of 18.
            </p>
          </section>

          {/* Section 7 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">7. Changes to This Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may update this Privacy Policy from time to time. If we make material changes, we will update the "Last Updated" date and may provide additional notice on the Platform.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Your continued use of the Platform after changes are posted constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* Section 8 */}
          <section className="mb-4">
            <h2 className="text-lg font-bold text-gray-900 mb-3">8. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-2">
              If you have questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <p className="text-gray-700 leading-relaxed">
              Email:{' '}
              <a
                href="mailto:info@saveonstackd.com"
                className="font-medium underline"
                style={{ color: '#F06F6B' }}
              >
                info@saveonstackd.com
              </a>
            </p>
          </section>

          {/* Back link */}
          <div className="mt-10 pt-6 border-t border-gray-100">
            <Link
              to="/"
              className="text-sm font-medium"
              style={{ color: '#F06F6B' }}
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
